import { type FC, useMemo } from "react";
import {
  Alert,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  FormBuilder,
  lockSchema,
  SECRET_PLACEHOLDER,
} from "@/shared/components/Form";
import {
  useCurrentPlatformQuery,
  useConnectCCBillMutation,
  useDisconnectCCBillMutation,
  useRotateCCBillWebhookSecretMutation,
} from "@/partner/entities/platforms";
import {
  interpolationFormat,
  resolveMutationErrorKey,
} from "@/partner/shared/lib";
import { makeCCBillSettingsSchema } from "../model/schemas";

/**
 * Коды отказа бекенда (extensions.code) и тексты под них, как у Stripe и
 * Paynet. Сообщение сервера наружу не идёт.
 */
const CONNECT_ERROR_KEYS: Record<string, string> = {
  INVALID_CCBILL_SETTINGS: "paymentSettings.ccbillErrors.invalidSettings",
  PAYMENT_PROVIDER_NOT_AVAILABLE: "paymentSettings.providerNotAllowedError",
};

const ROTATE_ERROR_KEYS: Record<string, string> = {
  CCBILL_NOT_CONNECTED: "paymentSettings.ccbillErrors.notConnected",
};

const CONNECT_FALLBACK_KEY = "paymentSettings.ccbillErrors.fallback";
const DISCONNECT_FALLBACK_KEY = "paymentSettings.disconnectError";
const ACTION_FALLBACK_KEY = "paymentSettings.actionError";

/**
 * Блок подключения CCBill, 1:1 по образцу PaynetSettingsCard, рядом с
 * которым рендерится (см. PlatformSettings.tsx). Salt наружу не отдаётся
 * вообще, даже маской (см. схему PlatformCCBillSettings): поле всегда
 * пустое, при переподключении его вводят заново.
 *
 * Отличие от Stripe и Paynet: адрес вебхука партнёр обязан вписать в
 * админку CCBill руками, поэтому он показан целиком (вместе с секретом в
 * пути) и рядом стоит кнопка перевыпуска секрета.
 */
export const CCBillSettingsCard: FC = () => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { data, refetch } = useCurrentPlatformQuery();

  const ccbill = data?.currentPlatform?.paymentSettings?.ccbill;
  // Пока CCBill подключён, поля только показывают сохранённые значения
  // (salt точками); редактировать можно после «Отключить».
  const locked = Boolean(ccbill?.connectedAt);
  const schema = useMemo(() => {
    const base = makeCCBillSettingsSchema(t);
    return locked ? lockSchema(base) : base;
  }, [t, locked]);

  const [connectCCBill, { loading: connecting }] = useConnectCCBillMutation({
    onCompleted: () => {
      enqueueSnackbar(t("paymentSettings.ccbillConnectedSnackbar"), {
        variant: "success",
      });
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(
        t(
          resolveMutationErrorKey(
            error,
            CONNECT_ERROR_KEYS,
            CONNECT_FALLBACK_KEY
          )
        ),
        { variant: "error" }
      );
    },
  });

  const [disconnectCCBill, { loading: disconnecting }] =
    useDisconnectCCBillMutation({
      onCompleted: () => {
        enqueueSnackbar(t("paymentSettings.ccbillDisconnectedSnackbar"), {
          variant: "success",
        });
        refetch();
      },
      onError: (error) =>
        enqueueSnackbar(
          t(resolveMutationErrorKey(error, {}, DISCONNECT_FALLBACK_KEY)),
          { variant: "error" }
        ),
    });

  const [rotateCCBillWebhookSecret, { loading: rotating }] =
    useRotateCCBillWebhookSecretMutation({
      onCompleted: () => {
        enqueueSnackbar(t("paymentSettings.ccbillSecretRotatedSnackbar"), {
          variant: "warning",
        });
        refetch();
      },
      onError: (error) =>
        enqueueSnackbar(
          t(
            resolveMutationErrorKey(
              error,
              ROTATE_ERROR_KEYS,
              ACTION_FALLBACK_KEY
            )
          ),
          { variant: "error" }
        ),
    });

  return (
    <Card sx={{ mt: 2 }}>
      <CardHeader
        title={t("paymentSettings.ccbillTitle")}
        action={
          ccbill?.connectedAt ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                color="success"
                label={t("paymentSettings.ccbillConnectedAt", {
                  date: interpolationFormat(
                    ccbill.connectedAt,
                    "dateTime",
                    i18n.language
                  ),
                })}
              />
              <Chip size="small" variant="outlined" label={ccbill.accnum} />
            </Stack>
          ) : (
            <Chip
              size="small"
              label={t("paymentSettings.ccbillNotConnected")}
            />
          )
        }
      />
      <CardContent>
        {ccbill?.webhookUrl && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>
              {t("paymentSettings.ccbillWebhookUrlLabel")}
            </AlertTitle>
            <Typography
              variant="body2"
              sx={{ wordBreak: "break-all", fontFamily: "monospace" }}
            >
              {ccbill.webhookUrl}
            </Typography>
            <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
              {t("paymentSettings.ccbillWebhookUrlHint")}
            </Typography>
          </Alert>
        )}
        {ccbill?.webhookUrl && (
          <Button
            size="small"
            color="warning"
            sx={{ mb: 2 }}
            disabled={rotating}
            onClick={() => rotateCCBillWebhookSecret()}
          >
            {t("paymentSettings.rotateCCBillSecretButton")}
          </Button>
        )}
        <FormBuilder
          key={locked ? "locked" : "editable"}
          formId="ccbill-settings-form"
          schema={schema}
          initialValues={{
            accnum: ccbill?.accnum ?? "",
            subaccount: ccbill?.subaccount ?? "",
            flexFormId: ccbill?.flexFormId ?? "",
            salt: locked ? SECRET_PLACEHOLDER : "",
            supportUrl: ccbill?.supportUrl ?? "",
            sandbox: ccbill?.sandbox ?? false,
          }}
          onSubmit={async (values) => {
            await connectCCBill({
              variables: {
                input: {
                  accnum: String(values.accnum),
                  subaccount: String(values.subaccount),
                  flexFormId: String(values.flexFormId),
                  salt: String(values.salt),
                  supportUrl: values.supportUrl
                    ? String(values.supportUrl)
                    : undefined,
                  sandbox: Boolean(values.sandbox),
                },
              },
            });
          }}
          submitButton={!locked}
          resetButton={false}
          submitButtonText={t("paymentSettings.connectCCBillButton")}
          disabled={connecting || locked}
          actionsAlign="end"
        />
        {ccbill?.connectedAt && (
          <Button
            variant="outlined"
            color="error"
            sx={{ mt: 2 }}
            disabled={disconnecting}
            onClick={() => disconnectCCBill()}
          >
            {t("paymentSettings.disconnectCCBillButton")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
